import React, {
  useCallback,
  useEffect,
  useMemo,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {
  RechargeCommissionRuleThresholds,
  RechargeCommissionRuleV2,
} from '../invitation.type';
import {
  GameCommissionDisplayItem,
  getGameCommissionDisplayList,
} from './game-commission.utils';
import {
  getRechargeCommissionDisplayList,
  getRechargeCommissionLevels,
} from './recharge-commission.utils';
import {formatCashbackThreshold} from './commission-rule.utils';
import {GameRebateFooter} from './GameRebateFooter';

const ruleBookImg = require('@/assets/imgs/invitation/ruleBook.webp');
const rulesPopupBgImg = require('@/assets/imgs/invitation/bg.webp');
const rulesBannerBgImg = require('@/assets/imgs/invitation/bb.webp');
const rulesBannerEllipseImg = require('@/assets/imgs/invitation/ellipse.webp');
const rulesTitleBoxImg = require('@/assets/imgs/invitation/box.webp');
const rulesTitleGoldImg = require('@/assets/imgs/invitation/gold.webp');
const rulesTitleMoneyPageImg = require('@/assets/imgs/invitation/moneyPage.webp');
const agentTeamImg = require('@/assets/imgs/invitation/agentTeam.webp');
const agentBgImg = require('@/assets/imgs/invitation/agentBg.webp');
const agentYouImg = require('@/assets/imgs/invitation/You.webp');
const agentAImg = require('@/assets/imgs/invitation/A.webp');
const agentBImg = require('@/assets/imgs/invitation/B.webp');
const agentCImg = require('@/assets/imgs/invitation/C.webp');
const agentRightImg = require('@/assets/imgs/invitation/Right.webp');
const closeImg = require('@/assets/imgs/invitation/close.webp');

export interface InvitationRulesPopupProps {
  visible: boolean;
  agentLevel?: number;
  initialSection?: InvitationRulesSection;
  rechargeRuleGroups?: RechargeCommissionRuleV2[];
  thresholds?: RechargeCommissionRuleThresholds;
  onClose: () => void;
}

export type InvitationRulesSection = 'referral' | 'deposit' | 'bet';

type Translate = ReturnType<typeof useTranslation>['t'];

type FaqAnswerBlock =
  | {
      type: 'text';
      text: string;
    }
  | {
      type: 'list';
      items: string[];
    }
  | {
      type: 'highlight';
      text: string;
    };

type FaqItem = {
  question: string;
  answer?: string;
  answerBlocks?: FaqAnswerBlock[];
};

type RuleThresholdKey = keyof RechargeCommissionRuleThresholds;

type RuleTextPart = {
  text: string;
  highlight?: boolean;
  threshold?: RuleThresholdKey;
  thresholdPrefix?: string;
};

type RuleContentBlock =
  | {
      type: 'text';
      text: string;
      thresholdRequirements?: RuleThresholdKey[];
    }
  | {
      type: 'list';
      items: RuleTextPart[][];
      ordered?: boolean;
    };

type RuleSection = {
  title: string;
  content?: string;
  lines?: string[];
  blocks?: RuleContentBlock[];
};

const faqList: FaqItem[] = [
  {
    question: 'invitation.rulesPopup.faq.who.question',
    answer: 'invitation.rulesPopup.faq.who.answer',
  },
  {
    question: 'invitation.rulesPopup.faq.benefits.question',
    answerBlocks: [
      {
        type: 'text',
        text: 'invitation.rulesPopup.faq.benefits.answer1',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.faq.benefits.answer2',
      },
    ],
  },
  {
    question: 'invitation.rulesPopup.faq.claim.question',
    answer: 'invitation.rulesPopup.faq.claim.answer',
  },
  {
    question: 'invitation.rulesPopup.faq.example.question',
    answerBlocks: [
      {
        type: 'text',
        text: 'invitation.rulesPopup.faq.example.answer1',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.faq.example.answer2',
      },
      {
        type: 'list',
        items: [
          'invitation.rulesPopup.faq.example.level1',
          'invitation.rulesPopup.faq.example.level2',
          'invitation.rulesPopup.faq.example.level3',
        ],
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.faq.example.answer3',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.faq.example.answer4',
      },
      {
        type: 'highlight',
        text: 'invitation.rulesPopup.faq.example.formula',
      },
    ],
  },
];

const sections: RuleSection[] = [
  {
    title: 'invitation.rulesPopup.sections.referral.title',
    blocks: [
      {
        type: 'text',
        text: 'invitation.rulesPopup.sections.referral.intro',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.common.requirements',
        thresholdRequirements: ['rechargeThreshold', 'betThreshold'],
      },
      {
        type: 'list',
        items: [
          [
            {text: 'invitation.rulesPopup.common.successfulDeposit'},
            {
              text: '₹200',
              highlight: true,
              threshold: 'rechargeThreshold',
              thresholdPrefix: '₹',
            },
          ],
          [
            {text: 'invitation.rulesPopup.common.validTurnover'},
            {
              text: '≥ ₹1,600',
              highlight: true,
              threshold: 'betThreshold',
              thresholdPrefix: '≥ ₹',
            },
          ],
        ],
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.common.rules',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          [{text: 'invitation.rulesPopup.sections.referral.rule1'}],
          [
            {
              text: 'invitation.rulesPopup.sections.referral.rule2',
            },
          ],
          [
            {
              text: 'invitation.rulesPopup.sections.referral.rule3',
            },
          ],
        ],
      },
    ],
  },
  {
    title: 'invitation.rulesPopup.sections.deposit.title',
    blocks: [
      {
        type: 'text',
        text: 'invitation.rulesPopup.sections.deposit.intro1',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.sections.deposit.intro2',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.common.requirements',
        thresholdRequirements: [
          'rechargeCommissionRechargeThreshold',
          'rechargeCommissionBetThreshold',
        ],
      },
      {
        type: 'list',
        items: [
          [
            {text: 'invitation.rulesPopup.common.successfulDeposit'},
            {
              text: '',
              highlight: true,
              threshold: 'rechargeCommissionRechargeThreshold',
              thresholdPrefix: '₹',
            },
          ],
          [
            {text: 'invitation.rulesPopup.common.validBettingAmount'},
            {
              text: '',
              highlight: true,
              threshold: 'rechargeCommissionBetThreshold',
              thresholdPrefix: '≥ ₹',
            },
          ],
        ],
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.sections.deposit.rule1',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.sections.deposit.rule2',
      },
    ],
  },
  {
    title: 'invitation.rulesPopup.sections.bet.title',
    blocks: [
      {
        type: 'text',
        text: 'invitation.rulesPopup.sections.bet.intro1',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.sections.bet.intro2',
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.common.requirements',
        thresholdRequirements: [
          'betCommissionRechargeThreshold',
          'betCommissionBetThreshold',
        ],
      },
      {
        type: 'list',
        items: [
          [
            {text: 'invitation.rulesPopup.common.successfulDeposit'},
            {
              text: '',
              highlight: true,
              threshold: 'betCommissionRechargeThreshold',
              thresholdPrefix: '₹',
            },
          ],
          [
            {text: 'invitation.rulesPopup.common.validTurnover'},
            {
              text: '',
              highlight: true,
              threshold: 'betCommissionBetThreshold',
              thresholdPrefix: '≥ ₹',
            },
          ],
        ],
      },
      {
        type: 'text',
        text: 'invitation.rulesPopup.common.rules',
      },
      {
        type: 'list',
        items: [
          [
            {
              text: 'invitation.rulesPopup.sections.bet.rule1',
            },
          ],
          [
            {
              text: 'invitation.rulesPopup.sections.bet.rule2',
            },
          ],
        ],
      },
    ],
  },
];

const depositColumnKey = 'invitation.rulesPopup.table.subordinateDeposit';

const agentLevels = [
  ['A1', 'You', 'invitation.rulesPopup.agentLevel.a'],
  ['B1', 'You', 'invitation.rulesPopup.agentLevel.b'],
  ['C1', 'You', 'invitation.rulesPopup.agentLevel.c'],
];

const getGameRuleRow = (item: GameCommissionDisplayItem) => [
  item.gameTypeName,
  item.commissionRate,
];
const scrollbarThumbHeight = 39;
const rulesAnchorOffsetRem = 3.125;
const rulesPopupBgStyle: React.CSSProperties = {
  backgroundImage: `url(${rulesPopupBgImg})`,
  backgroundPosition: 'top center',
  backgroundSize: '100% auto',
};

const getRootRemInPixels = () => {
  const rootFontSize = window.getComputedStyle(
    document.documentElement,
  ).fontSize;
  const remInPixels = Number.parseFloat(rootFontSize);
  return Number.isFinite(remInPixels) ? remInPixels : 16;
};

const getAgentIcon = (name: string) => {
  if (name === 'You') {
    return agentYouImg;
  }
  if (name.startsWith('A')) {
    return agentAImg;
  }
  if (name.startsWith('B')) {
    return agentBImg;
  }
  return agentCImg;
};

const RulesTitle: React.FC<{
  children: React.ReactNode;
  icon?: string;
}> = ({children, icon = rulesTitleBoxImg}) => {
  return (
    <div className="mx-auto flex h-[1.75rem] w-[14.25rem] items-center justify-center rounded-[0.875rem] border border-[#744c24] bg-[linear-gradient(180deg,#4a3218_0%,#2b1b10_100%)]">
      <img
        alt=""
        className="mr-[0.1875rem] h-[1.1875rem] w-[1.25rem] object-contain"
        src={icon}
      />
      <span className="bg-gradient-to-b from-[#fad167] to-[#ebae41] bg-clip-text font-['Inter'] text-[0.875rem] font-bold leading-normal text-transparent">
        {children}
      </span>
    </div>
  );
};

const RulesPopupHeader = () => {
  const {t} = useTranslation();

  return (
    <div className="pointer-events-none absolute left-1/2 top-[-0.0625rem] z-[20] flex h-[1.75rem] w-[10.3125rem] -translate-x-1/2 items-center justify-center overflow-visible">
      <svg
        className="absolute inset-0 size-full"
        fill="none"
        viewBox="0 0 165 28"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1 0.5H164L154.6 22.4C153.2 25.5 150.2 27.5 146.8 27.5H18.2C14.8 27.5 11.8 25.5 10.4 22.4L1 0.5Z"
          fill="url(#rules-popup-header-fill)"
          stroke="url(#rules-popup-header-stroke)"
        />
        <defs>
          <linearGradient
            id="rules-popup-header-fill"
            x1="82.5"
            x2="82.5"
            y1="0.5"
            y2="27.5"
            gradientUnits="userSpaceOnUse">
            <stop stopColor="#402d19" />
            <stop offset="0.35" stopColor="#2b1d11" />
            <stop offset="0.62" stopColor="#23160d" />
            <stop offset="1" stopColor="#2e1e12" />
          </linearGradient>
          <linearGradient
            id="rules-popup-header-stroke"
            x1="82.5"
            x2="82.5"
            y1="0"
            y2="28"
            gradientUnits="userSpaceOnUse">
            <stop stopColor="#8e6a34" />
            <stop offset="0.78" stopColor="#8e6a34" />
            <stop offset="1" stopColor="#e9d597" />
          </linearGradient>
        </defs>
      </svg>
      <div className="relative z-[1] flex h-[1.5rem] items-center gap-[0.3125rem]">
        <img
          alt=""
          className="size-[1.125rem] object-contain"
          src={ruleBookImg}
        />
        <span className="bg-gradient-to-b from-[#fad167] to-[#ebae41] bg-clip-text font-['Inter'] text-[0.875rem] font-bold leading-normal text-transparent">
          {t('invitation.home.rules')}
        </span>
      </div>
    </div>
  );
};

const BannerTextLine: React.FC<{
  children: React.ReactNode;
  className: string;
}> = ({children, className}) => {
  return (
    <span
      className={`${className} absolute z-[1] block whitespace-nowrap text-center font-['Inter'] text-[0.875rem] font-bold not-italic leading-normal`}>
      <span className="absolute inset-0 text-white [text-shadow:0_0.125rem_0.1125rem_rgba(0,0,0,0.25)]">
        {children}
      </span>
      <span className="relative bg-gradient-to-b from-white from-[32.353%] to-[#f3b544] to-[90.196%] bg-clip-text text-transparent">
        {children}
      </span>
    </span>
  );
};

const AgentLevelTitle = () => {
  const {t} = useTranslation();

  return (
    <div className="absolute left-1/2 top-[-0.8505rem] z-[2] flex h-[1.75rem] w-[14.25rem] -translate-x-1/2 items-center justify-center rounded-[0.875rem] border border-[rgba(255,255,255,0.1)] bg-[radial-gradient(ellipse_at_center,#563005_0%,#4f2a02_100%)]">
      <div className="flex items-center gap-[0.3125rem]">
        <img
          alt=""
          className="h-[0.875rem] w-[1.25rem] object-contain"
          src={agentTeamImg}
        />
        <span className="font-['Inter'] text-[0.875rem] font-semibold leading-normal text-[#f1bb50]">
          {t('invitation.rulesPopup.agentLevel.title')}
        </span>
      </div>
    </div>
  );
};

const AgentNode: React.FC<{
  name: string;
  compact?: boolean;
}> = ({name, compact}) => {
  const {t} = useTranslation();
  const isYou = name === 'You';
  const isC = name.startsWith('C');
  const textSizeClassName =
    compact && isYou ? 'text-[0.625rem]' : 'text-[0.75rem]';
  const textEffectClassName =
    compact && isYou
      ? ''
      : isYou
      ? '[-webkit-text-stroke-color:#F1BB50] [-webkit-text-stroke-width:0.03125rem]'
      : '[text-shadow:0_0.0625rem_0_#1c1008,0.0625rem_0_0_#1c1008,-0.0625rem_0_0_#1c1008,0_-0.0625rem_0_#1c1008]';

  return (
    <div
      className={`relative z-[2] flex flex-col items-center justify-start ${
        compact ? 'min-w-[1.75rem]' : isYou ? 'min-w-[2.5rem]' : 'min-w-[2rem]'
      }`}>
      <img
        alt=""
        className={`object-contain ${
          compact
            ? isC
              ? 'h-[1.4423rem] w-[1.25rem]'
              : isYou
              ? 'h-[1.25rem] w-[1.0833rem]'
              : 'h-[1.125rem] w-[0.9375rem]'
            : isYou
            ? 'h-[1.875rem] w-[1.625rem]'
            : 'h-[1.5rem] w-[1.25rem]'
        }`}
        src={getAgentIcon(name)}
      />
      <span
        className={`font-['Inter'] font-medium leading-[normal] text-white ${textSizeClassName} ${
          compact ? 'mt-[0.0625rem]' : 'mt-[0.125rem]'
        } ${textEffectClassName}`}>
        {isYou ? t('invitation.rulesPopup.agentLevel.you') : name}
      </span>
    </div>
  );
};

const treeNodes = [
  {name: 'You', left: '10rem', top: '1.5625rem'},
  {name: 'A1', left: '4.875rem', top: '5rem'},
  {name: 'A2', left: '15.125rem', top: '5rem'},
  {name: 'B1', left: '2.625rem', top: '7.5625rem'},
  {name: 'B2', left: '7.125rem', top: '7.5625rem'},
  {name: 'B3', left: '12.9375rem', top: '7.5625rem'},
  {name: 'B4', left: '17.4375rem', top: '7.5625rem'},
  {name: 'C1', left: '1.25rem', top: '10.125rem'},
  {name: 'C2', left: '3.75rem', top: '10.125rem'},
  {name: 'C3', left: '5.75rem', top: '10.125rem'},
  {name: 'C4', left: '8.25rem', top: '10.125rem'},
  {name: 'C5', left: '11.5625rem', top: '10.125rem'},
  {name: 'C6', left: '14.0625rem', top: '10.125rem'},
  {name: 'C7', left: '16.0625rem', top: '10.125rem'},
  {name: 'C8', left: '18.5625rem', top: '10.125rem'},
];

const treeArrows = [
  {left: '8.5rem', top: '3.0625rem', width: '3.75rem', rotate: '153deg'},
  {left: '11.5rem', top: '3.0625rem', width: '3.75rem', rotate: '27deg'},

  {left: '4.125rem', top: '6.25rem', width: '1.5625rem', rotate: '138deg'},
  {left: '5.625rem', top: '6.25rem', width: '1.5625rem', rotate: '42deg'},
  {left: '14.375rem', top: '6.25rem', width: '1.5625rem', rotate: '138deg'},
  {left: '15.875rem', top: '6.25rem', width: '1.5625rem', rotate: '42deg'},

  {left: '2.25rem', top: '9.0625rem', width: '1.1875rem', rotate: '140deg'},
  {left: '2.875rem', top: '9.0625rem', width: '1.1875rem', rotate: '40deg'},
  {left: '6.75rem', top: '9.0625rem', width: '1.1875rem', rotate: '140deg'},
  {left: '7.375rem', top: '9.0625rem', width: '1.1875rem', rotate: '40deg'},
  {left: '12.5625rem', top: '9.0625rem', width: '1.1875rem', rotate: '140deg'},
  {left: '13.1875rem', top: '9.0625rem', width: '1.1875rem', rotate: '40deg'},
  {left: '17.0625rem', top: '9.0625rem', width: '1.1875rem', rotate: '140deg'},
  {left: '17.6875rem', top: '9.0625rem', width: '1.1875rem', rotate: '40deg'},
];

const TreeArrow: React.FC<{
  left: string;
  top: string;
  width: string;
  rotate: string;
}> = ({left, top, width, rotate}) => {
  return (
    <span
      className="pointer-events-none absolute z-[1] h-[0.125rem] origin-left rounded-full bg-[#f2d27a]"
      style={{
        left,
        top,
        width,
        transform: `rotate(${rotate})`,
      }}>
      <span className="absolute right-[-0.21875rem] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[0.25rem] border-l-[0.4375rem] border-y-transparent border-l-[#f2d27a]" />
    </span>
  );
};

const AgentTree: React.FC<{standalone?: boolean}> = ({standalone}) => {
  const className = standalone
    ? 'relative left-1/2 z-[3] h-[14.314375rem] w-[20rem] max-w-full origin-top -translate-x-1/2 scale-[1.06] overflow-visible'
    : 'relative z-[3] h-[13.25rem] overflow-visible';

  return (
    <div className={className}>
      {treeArrows.map((arrow, index) => (
        <TreeArrow
          key={`${arrow.left}-${arrow.top}-${index}`}
          left={arrow.left}
          top={arrow.top}
          width={arrow.width}
          rotate={arrow.rotate}
        />
      ))}
      {treeNodes.map(node => (
        <div
          key={node.name}
          className="absolute z-[2] -translate-x-1/2"
          style={{left: node.left, top: node.top}}>
          <AgentNode name={node.name} />
        </div>
      ))}
    </div>
  );
};

const CommissionTable: React.FC<{
  columns: string[];
  rows: string[][];
  compact?: boolean;
  footer?: React.ReactNode;
  fullWidth?: boolean;
  gameRebateStyle?: boolean;
  title?: string;
}> = ({columns, rows, compact, footer, fullWidth, gameRebateStyle, title}) => {
  const tableClassName = gameRebateStyle
    ? fullWidth
      ? 'bg-transparent'
      : 'rounded-[0.625rem] border border-[rgba(255,255,255,0.23)] bg-[#2a1806]'
    : 'rounded-b-[0.5625rem] bg-[#372e27]';
  const tableWidthClassName = fullWidth
    ? '-mx-[0.375rem] w-[calc(100%+0.75rem)] max-w-none'
    : '-mx-[0.375rem] w-[19.8125rem] max-w-[calc(100%+0.75rem)]';
  const headerClassName = gameRebateStyle
    ? 'h-[1.75rem] bg-[rgba(217,217,217,0.11)]'
    : 'h-[2.9375rem] bg-[rgba(255,255,255,0.1)]';
  const rowClassName = 'h-[1.75rem]';
  const firstColumnClassName = gameRebateStyle
    ? 'w-1/2 shrink-0'
    : compact
    ? 'w-[27.13%] shrink-0'
    : 'w-[32%] shrink-0';
  const commissionColumnMinWidthRem = compact ? 4.8125 : 4.5;
  const commissionColumns = columns.slice(1);
  const hasScrollableCommissionColumns = commissionColumns.length > 3;
  const shouldFillCommissionWidth =
    fullWidth && !hasScrollableCommissionColumns;
  const commissionContentStyle: React.CSSProperties = shouldFillCommissionWidth
    ? {minWidth: '100%', width: '100%'}
    : fullWidth
    ? {
        minWidth: `${(commissionColumns.length / 3) * 100}%`,
        width: `${(commissionColumns.length / 3) * 100}%`,
      }
    : {
        minWidth: '100%',
        width: `${commissionColumns.length * commissionColumnMinWidthRem}rem`,
      };
  const commissionGridStyle: React.CSSProperties = {
    gridTemplateColumns: `repeat(${commissionColumns.length}, minmax(0, 1fr))`,
  };
  const headerCellBaseClassName =
    "min-w-0 overflow-hidden whitespace-normal break-words text-center font-['Inter'] font-normal text-[#ccc]";
  const firstHeaderCellClassName = compact
    ? `${headerCellBaseClassName} px-0 text-[0.75rem] leading-[normal]`
    : `${headerCellBaseClassName} px-[0.1875rem] text-[0.75rem] leading-[normal]`;

  return (
    <div className={`overflow-hidden ${tableWidthClassName} ${tableClassName}`}>
      {title ? (
        <div className="flex items-center justify-start whitespace-nowrap px-3 py-3 text-left font-['Inter'] text-[0.875rem] font-semibold not-italic leading-[normal] text-white [word-break:break-word]">
          {title}
        </div>
      ) : null}
      <div className="flex">
        <div
          className={`${firstColumnClassName} ${
            commissionColumns.length > 3
              ? 'border-r border-[rgba(255,255,255,0.16)]'
              : ''
          }`}>
          <div
            className={`flex items-center justify-center ${headerClassName} ${firstHeaderCellClassName}`}>
            {columns[0]}
          </div>
          {rows.map((row, rowIndex) => (
            <div
              key={`${row[0]}-${rowIndex}`}
              className={`flex items-center justify-center whitespace-nowrap px-[0.1875rem] text-center font-['Inter'] text-[0.75rem] font-normal leading-normal text-white ${rowClassName} ${
                rowIndex % 2 === 1 ? 'bg-[rgba(255,255,255,0.1)]' : ''
              }`}>
              {row[0]}
            </div>
          ))}
        </div>
        <div
          className={
            shouldFillCommissionWidth
              ? 'min-w-0 flex-1 overflow-hidden'
              : 'min-w-0 flex-1 overflow-x-auto overflow-y-hidden [overscroll-behavior-x:none] [scrollbar-width:none] [-webkit-overflow-scrolling:auto] [&::-webkit-scrollbar]:hidden'
          }>
          <div style={commissionContentStyle}>
            <div
              className={`grid w-full items-center ${headerClassName}`}
              style={commissionGridStyle}>
              {commissionColumns.map(column => (
                <div
                  key={column}
                  className={`flex h-full items-center justify-center ${firstHeaderCellClassName}`}>
                  {column}
                </div>
              ))}
            </div>
            {rows.map((row, rowIndex) => (
              <div
                key={`${row[0]}-${rowIndex}`}
                className={`grid w-full items-center ${rowClassName} ${
                  rowIndex % 2 === 1 ? 'bg-[rgba(255,255,255,0.1)]' : ''
                }`}
                style={commissionGridStyle}>
                {row.slice(1).map((cell, columnIndex) => (
                  <div
                    key={`${row[0]}-${columnIndex}`}
                    className="flex h-full min-w-0 items-center justify-center whitespace-nowrap px-[0.1875rem] text-center font-['Inter'] text-[0.75rem] font-normal leading-normal text-white">
                    {cell}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      {footer}
    </div>
  );
};

const AgentLevelGuide: React.FC<{standalone?: boolean}> = ({standalone}) => {
  const {t} = useTranslation();
  const labelClassName = standalone
    ? "min-w-0 flex-1 break-words text-center font-['Inter'] text-[0.875rem] font-medium not-italic leading-normal text-white"
    : "font-['Inter'] text-[0.75rem] font-medium leading-normal text-white";

  return (
    <section className="relative overflow-visible rounded-t-[0.625rem] bg-[linear-gradient(180deg,#2e1f11_0%,#1d1612_100%)] pb-0 font-['Inter']">
      <AgentLevelTitle />
      <img
        alt=""
        className="pointer-events-none absolute left-0 top-0 h-[13.25rem] w-full rounded-t-[0.625rem] object-fill"
        src={agentBgImg}
      />
      <AgentTree standalone={standalone} />
      <div className="relative z-[4] flex flex-col gap-2 px-[0.625rem] pt-0">
        {agentLevels.map(([from, to, label]) => (
          <div
            key={label}
            className="flex h-[2.5rem] items-center justify-between rounded-[0.625rem] border border-[#744c24] bg-[#342720] px-[0.875rem]">
            <div className="flex min-w-[6.25rem] items-center justify-between">
              <AgentNode name={from} compact />
              <img
                alt=""
                className="h-[2.5rem] w-[2.75rem] object-contain"
                src={agentRightImg}
              />
              <AgentNode name={to} compact />
            </div>
            <span className={labelClassName}>{t(label)}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const FaqAnswer: React.FC<{item: FaqItem; t: Translate}> = ({item, t}) => {
  const baseClassName =
    "whitespace-pre-wrap font-['Inter'] text-[0.75rem] font-normal not-italic leading-[1rem] text-white [word-break:break-word]";

  if (!item.answerBlocks) {
    return (
      <p className={`mt-[0.625rem] ${baseClassName}`}>
        {item.answer ? t(item.answer) : ''}
      </p>
    );
  }

  return (
    <div className={`mt-[0.625rem] ${baseClassName}`}>
      {item.answerBlocks.map((block, index) => {
        if (block.type === 'list') {
          return (
            <ul
              key={`${block.type}-${index}`}
              className="mb-0 list-disc pl-[1.125rem]">
              {block.items.map(text => (
                <li key={text} className="mb-0">
                  <span>{t(text)}</span>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p
            key={`${block.type}-${index}`}
            className={`mb-0 ${
              block.type === 'highlight' ? 'text-[#f1bb50]' : ''
            }`}>
            {t(block.text)}
          </p>
        );
      })}
    </div>
  );
};

export interface InvitationRulesContentProps {
  agentLevel?: number;
  initialSection?: InvitationRulesSection;
  rechargeRuleGroups?: RechargeCommissionRuleV2[];
  standalone?: boolean;
  thresholds?: RechargeCommissionRuleThresholds;
}

export const InvitationRulesContent: React.FC<InvitationRulesContentProps> = ({
  agentLevel,
  initialSection,
  rechargeRuleGroups,
  standalone = false,
  thresholds,
}) => {
  const {t} = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const referralRef = useRef<HTMLElement>(null);
  const depositRef = useRef<HTMLElement>(null);
  const betRef = useRef<HTMLElement>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const {depositColumns, depositRows} = useMemo(() => {
    const list = getRechargeCommissionDisplayList(
      rechargeRuleGroups,
      agentLevel,
    );
    const levels = getRechargeCommissionLevels(list);

    return {
      depositColumns: [
        t(depositColumnKey),
        ...levels.map(level => t('invitation.home.level-commission', {level})),
      ],
      depositRows: list.map(item => [
        item.displayDeposit,
        ...levels.map(
          level => item[`level${level}Rate` as `level${number}Rate`] || '-',
        ),
      ]),
    };
  }, [agentLevel, rechargeRuleGroups, t]);
  const gameRows = useMemo(() => {
    const gameList = getGameCommissionDisplayList(
      rechargeRuleGroups,
      agentLevel,
    );
    return gameList.map(getGameRuleRow);
  }, [agentLevel, rechargeRuleGroups]);
  const gameColumns = [
    t('invitation.home.game-column'),
    t('invitation.home.commission-rate-column'),
  ];

  const updateScrollThumb = useCallback(() => {
    const scrollElement = scrollRef.current;
    const trackElement = trackRef.current;
    if (!scrollElement || !trackElement) {
      return;
    }
    const scrollableHeight =
      scrollElement.scrollHeight - scrollElement.clientHeight;
    if (scrollableHeight <= 0) {
      setThumbTop(0);
      return;
    }
    const maxThumbTop = Math.max(
      0,
      trackElement.clientHeight - scrollbarThumbHeight,
    );
    setThumbTop((scrollElement.scrollTop / scrollableHeight) * maxThumbTop);
  }, []);

  useEffect(() => {
    if (standalone) {
      return;
    }
    updateScrollThumb();
    window.addEventListener('resize', updateScrollThumb);
    return () => {
      window.removeEventListener('resize', updateScrollThumb);
    };
  }, [standalone, updateScrollThumb]);

  useLayoutEffect(() => {
    if (standalone) {
      return;
    }
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const targetMap = {
      referral: referralRef.current,
      deposit: depositRef.current,
      bet: betRef.current,
    };
    const targetElement = initialSection
      ? targetMap[initialSection]
      : undefined;

    if (!targetElement) {
      scrollElement.scrollTop = 0;
      updateScrollThumb();
      return;
    }

    const targetTop =
      targetElement.getBoundingClientRect().top -
      scrollElement.getBoundingClientRect().top +
      scrollElement.scrollTop -
      rulesAnchorOffsetRem * getRootRemInPixels();
    scrollElement.scrollTop = Math.max(0, targetTop);
    updateScrollThumb();
  }, [initialSection, standalone, updateScrollThumb]);

  const containerClassName = standalone
    ? 'relative min-h-full w-full overflow-hidden bg-[linear-gradient(180deg,#230d07_0%,#2e140b_100%)]'
    : 'relative h-[calc(100vh-12.5rem)] max-h-[33.8125rem] w-[21.4375rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[0.9375rem] border border-solid border-[rgba(251,200,98,0.5)] bg-[#230d07] bg-no-repeat';
  const contentClassName = standalone
    ? 'px-3 pb-4'
    : 'h-full overflow-y-auto px-3 pb-4 pt-[1.75rem] [overscroll-behavior-y:none] [scrollbar-width:none] [-webkit-overflow-scrolling:auto] [&::-webkit-scrollbar]:hidden';
  const ruleCardSurfaceClassName = standalone
    ? 'rounded-[0.75rem] border-[0.0683125rem] border-solid border-[#744c25] bg-[linear-gradient(180deg,#2e1f11_0%,#1d1612_100%)]'
    : 'rounded-[0.625rem] border border-[#744c25] bg-[rgba(29,22,18,0.88)]';

  return (
    <div
      className={containerClassName}
      style={standalone ? undefined : rulesPopupBgStyle}>
      {!standalone ? <RulesPopupHeader /> : null}
      <div
        ref={scrollRef}
        className={contentClassName}
        onScroll={standalone ? undefined : updateScrollThumb}>
        <div className="relative mt-3 h-[4.5625rem] overflow-hidden rounded-lg text-center">
          <img
            alt=""
            className="absolute inset-0 size-full object-cover"
            src={rulesBannerBgImg}
          />
          <img
            alt=""
            className="pointer-events-none absolute left-[11.375rem] top-[2.3125rem] z-[1] h-[1.6875rem] w-[7.75rem] max-w-none"
            src={rulesBannerEllipseImg}
          />
          <BannerTextLine className="left-[1.5rem] top-[1.0625rem] w-[17rem]">
            {t('invitation.rulesPopup.banner.line1')}
          </BannerTextLine>
          <BannerTextLine className="left-[2.125rem] top-[2.4375rem] w-[15.8125rem]">
            {t('invitation.rulesPopup.banner.line2')}
          </BannerTextLine>
        </div>

        <div
          className={
            standalone
              ? 'relative mt-[1.75rem] overflow-visible rounded-[0.75rem] border-[0.0683125rem] border-solid border-[#744c25] bg-[linear-gradient(180deg,#2e1f11_0%,#1d1612_100%)]'
              : 'relative mt-[1.75rem] overflow-visible rounded-t-[0.625rem] border-x border-t border-[#744c25] bg-[#1d1612]'
          }>
          <AgentLevelGuide standalone={standalone} />

          <div className="relative mt-[0.875rem]">
            <section className="relative z-[1] rounded-[0.5625rem] border-b border-solid border-[#744c25] bg-[#3e3229] px-[0.9375rem] pb-[0.875rem] pt-[1rem]">
              {faqList.map((item, index) => (
                <div
                  key={item.question}
                  className={`${
                    index > 0
                      ? 'mt-[0.875rem] border-t border-[rgba(255,255,255,0.08)] pt-[0.875rem]'
                      : ''
                  }`}>
                  <h3 className="whitespace-nowrap text-left font-['Inter'] text-[0.75rem] font-normal not-italic leading-normal text-white [word-break:break-word]">
                    {t(item.question)}
                  </h3>
                  <FaqAnswer item={item} t={t} />
                </div>
              ))}
            </section>
          </div>
        </div>

        <section
          ref={referralRef}
          className={`mt-[1.75rem] ${ruleCardSurfaceClassName} px-[0.375rem] pb-[0.625rem] pt-0`}>
          <div className="-mt-[0.875rem]">
            <RulesTitle>{t(sections[0].title)}</RulesTitle>
          </div>
          <RuleParagraph
            content={sections[0].content}
            lines={sections[0].lines}
            blocks={sections[0].blocks}
            t={t}
            thresholds={thresholds}
          />
        </section>

        <section
          ref={depositRef}
          className={`mt-[1.75rem] ${ruleCardSurfaceClassName} px-[0.375rem] pb-0 pt-0`}>
          <div className="-mt-[0.875rem]">
            <RulesTitle icon={rulesTitleGoldImg}>
              {t(sections[1].title)}
            </RulesTitle>
          </div>
          <RuleParagraph
            content={sections[1].content}
            lines={sections[1].lines}
            blocks={sections[1].blocks}
            t={t}
            thresholds={thresholds}
          />
          {depositRows.length ? (
            <div className="mt-3">
              <CommissionTable
                columns={depositColumns}
                rows={depositRows}
                compact
                fullWidth={standalone}
              />
            </div>
          ) : null}
        </section>

        <section
          ref={betRef}
          className={`mt-[1.75rem] ${ruleCardSurfaceClassName} px-[0.375rem] pb-0 pt-0`}>
          <div className="-mt-[0.875rem]">
            <RulesTitle icon={rulesTitleMoneyPageImg}>
              {standalone
                ? t('invitation.home.commission-rate-title')
                : t(sections[2].title)}
            </RulesTitle>
          </div>
          {!standalone ? (
            <RuleParagraph
              content={sections[2].content}
              lines={sections[2].lines}
              blocks={sections[2].blocks}
              t={t}
              thresholds={thresholds}
            />
          ) : null}
          {gameRows.length ? (
            <div className={standalone ? 'mt-0' : 'mt-3'}>
              <CommissionTable
                columns={gameColumns}
                footer={standalone ? <GameRebateFooter /> : undefined}
                fullWidth={standalone}
                rows={gameRows}
                gameRebateStyle
                title={
                  standalone
                    ? t('invitation.home.game-rebate-title')
                    : undefined
                }
              />
            </div>
          ) : null}
        </section>
      </div>
      {!standalone ? (
        <div
          ref={trackRef}
          className="pointer-events-none absolute bottom-[1.125rem] right-[0.3125rem] top-[2.5rem] w-[0.1875rem] overflow-hidden rounded-[0.5rem] bg-[#503e39]">
          <div
            className="h-[2.4375rem] w-full rounded-[0.09375rem] bg-[#f1bb50]"
            style={{transform: `translateY(${thumbTop}px)`}}
          />
        </div>
      ) : null}
    </div>
  );
};

const getRuleTextPartText = (
  part: RuleTextPart,
  t: Translate,
  thresholds?: RechargeCommissionRuleThresholds,
) => {
  if (!part.threshold) {
    return part.text ? t(part.text) : '';
  }

  const amount = formatCashbackThreshold(thresholds?.[part.threshold]);
  return amount == null ? '' : `${part.thresholdPrefix || ''}${amount}`;
};

const RuleParagraph: React.FC<{
  content?: string;
  lines?: string[];
  blocks?: RuleContentBlock[];
  t: Translate;
  thresholds?: RechargeCommissionRuleThresholds;
}> = ({content, lines, blocks, t, thresholds}) => {
  const baseClassName =
    "px-[0.375rem] pt-3 font-['Inter'] text-[0.75rem] font-normal leading-[1rem] text-white [word-break:break-word]";
  const hasThreshold = (threshold: RuleThresholdKey) =>
    formatCashbackThreshold(thresholds?.[threshold]) != null;

  if (blocks) {
    return (
      <div className={baseClassName}>
        {blocks.map((block, index) => {
          if (block.type === 'list') {
            const ListTag = block.ordered ? 'ol' : 'ul';
            const visibleItems = block.items.filter(item => {
              const threshold = item.find(part => part.threshold)?.threshold;
              if (!threshold) {
                return true;
              }
              return hasThreshold(threshold);
            });

            if (!visibleItems.length) {
              return null;
            }

            return (
              <ListTag
                key={`${block.type}-${index}`}
                className={`mb-0 pl-[1.125rem] ${
                  block.ordered ? 'list-decimal' : 'list-disc'
                }`}>
                {visibleItems.map((item, itemIndex) => (
                  <li
                    key={`${block.type}-${index}-${itemIndex}`}
                    className="mb-0">
                    {item.map(part => (
                      <span
                        key={`${part.text}-${
                          part.highlight ? 'highlight' : 'normal'
                        }`}
                        className={part.highlight ? 'text-[#f1bb50]' : ''}>
                        {getRuleTextPartText(part, t, thresholds)}
                      </span>
                    ))}
                  </li>
                ))}
              </ListTag>
            );
          }

          if (
            block.thresholdRequirements?.every(
              threshold => !hasThreshold(threshold),
            )
          ) {
            return null;
          }

          return (
            <p
              key={`${block.type}-${index}`}
              className="mb-0 whitespace-pre-wrap">
              {t(block.text)}
            </p>
          );
        })}
      </div>
    );
  }

  return (
    <div className="px-[0.375rem] pt-3 font-['Inter'] text-[0.75rem] font-normal leading-[1.25rem] text-[#d7c6b6]">
      <p>{content ? t(content) : ''}</p>
      <div className="mt-[0.5rem] flex flex-col">
        {lines?.map(line => (
          <span key={line}>{t(line)}</span>
        ))}
      </div>
    </div>
  );
};

const InvitationRulesPopup: React.FC<InvitationRulesPopupProps> = ({
  visible,
  agentLevel,
  initialSection,
  rechargeRuleGroups,
  thresholds,
  onClose,
}) => {
  useEffect(() => {
    if (!visible) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  const popup = (
    <div
      className="fixed inset-0 z-[11140] flex justify-center bg-[rgba(0,0,0,0.65)] px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}>
      <div
        className="relative mt-[7.5rem] h-[calc(100vh-12.5rem)] max-h-[33.8125rem] w-[21.4375rem] max-w-[calc(100vw-2rem)]"
        onClick={event => event.stopPropagation()}>
        <button
          type="button"
          aria-label="Close rules"
          className="absolute right-0 top-[-2.8125rem] z-[2] flex size-[1.5rem] items-center justify-center border-0 bg-transparent p-0"
          onClick={onClose}>
          <img alt="" className="size-[1.5rem] object-contain" src={closeImg} />
        </button>
        <InvitationRulesContent
          agentLevel={agentLevel}
          initialSection={initialSection}
          rechargeRuleGroups={rechargeRuleGroups}
          thresholds={thresholds}
        />
      </div>
    </div>
  );

  return createPortal(popup, document.body);
};

export default InvitationRulesPopup;
